/* eslint-disable react-hooks-ext/exhaustive-deps -- TODO(react-hooks): 2 hook(s) need dependency review; fix each and remove this directive */
import React, { useEffect, useRef, useState } from "react";
import { useTranslation } from "@i18n";
import { fileViewModes } from "./constants";
import useFileManagerShared from "./useFileManagerShared";
import type { SlickgridReactInstance } from "slickgrid-react";
import type { SlickEventData } from "@slickgrid-universal/common";
import type { ContextMenuItem, FileManagerHandle } from "./FileManagerComponent";
import type {
  FileManagerData,
  FileManagerRequestArgs,
  FileManagerRow,
  FileManagerStateUpdate,
} from "./fileSystemServer.schema";

/**
 * A context-menu entry as this hook reads and writes it: the shared item plus the `extensions`
 * allow-list the file manager tags options with and the per-selection `disabled` flag set below.
 */
type FileContextMenuItem = ContextMenuItem & { disabled?: boolean; extensions?: string[] };

/** The context-menu configuration `FileManagerComponent` passes down. */
interface FileContextMenuSettings {
  file: FileContextMenuItem[];
  folder: FileContextMenuItem[];
  layout: FileContextMenuItem[];
  visible: boolean;
}

/** State of the MUI context menu this hook drives; `null` position/options mean "closed". */
interface FileMenuState {
  open: boolean;
  source: string;
  position: { top: number; left: number } | null;
  data?: FileManagerRow | null;
  options: FileContextMenuItem[] | null;
}

/**
 * The two coordinate fields the context menu positions itself with. Both React's synthetic mouse
 * event and slickgrid's `SlickEventData` carry them (slickgrid declares them optional).
 */
type ContextMenuEvent = { pageX?: number; pageY?: number };

/** The files grid's imperative handle as the caller holds it. */
type FilesGridRef = React.RefObject<FileManagerHandle | null>;

/** Method shorthand on the callbacks — see the note in `useFileManagerShared`. */
interface UseFileManagerFilesProps {
  setSelectedFolder(folder: FileManagerRow): void;
  onFileClick?(clickedRow: FileManagerRow): void;
  onFileSelect?(files: FileManagerRow[]): void;
  fileManagerData?: FileManagerData;
  contextMenuSettings?: FileContextMenuSettings;
  /**
   * Returns the ids of the context-menu entries to disable. The parameter is optional because the
   * layout (empty-space) menu calls this with no selection at all.
   */
  menuOpen?(selectedFiles?: FileManagerRow[]): string[];
  /** Built against the caller's own row type; only forwarded to the grid. */
  fileColumns?: object[];
  tempFolder?: FileManagerRow;
  /** `null` is the "no active search" sentinel the debounce state starts in. */
  handleSearchFiles?(query: string | null): void;
  onFileLoad?(files: FileManagerRow[]): void;
  beforeSending?(args: FileManagerRequestArgs): void;
  allowDragAndDropUpload?: boolean;
  selectedFolder?: FileManagerRow;
  filesViewMode?: string;
  setSelectedFilesInIconsMode?(update: FileManagerStateUpdate<FileManagerRow[]>): void;
  selectedFilesInIconsMode?: FileManagerRow[];
  setIsViewRendered?(rendered: boolean): void;
  isViewRendered?: boolean;
  disableDragAndDropUpload?: boolean;
  preSearchText?: string;
}

function useFileManagerFiles({
  setSelectedFolder,
  onFileClick = () => {},
  onFileSelect = () => {},
  fileManagerData = {},
  contextMenuSettings,
  menuOpen = () => [],
  fileColumns = [],
  tempFolder = {},
  handleSearchFiles = () => {},
  onFileLoad = () => {},
  beforeSending = () => {},
  allowDragAndDropUpload = false,
  selectedFolder = {},
  filesViewMode = "DETAILED_FILES",
  setSelectedFilesInIconsMode = () => {},
  selectedFilesInIconsMode = [],
  setIsViewRendered = () => {},
  isViewRendered: _isViewRendered = false,
  disableDragAndDropUpload = false,
  preSearchText = "",
}: UseFileManagerFilesProps) {
  const { t } = useTranslation();

  // Use shared hook for platform-agnostic logic (breadcrumbs, search, file filtering)
  const {
    query,
    setQuery,
    debounced,
    setDebounced,
    showPathColumn,
    setShowPathColumn,
    filesData,
    setFilesData,
    breadCrumbData,
    setBreadCrumbData,
    isFirstRender,
    setIsFirstRender,
  } = useFileManagerShared({
    setSelectedFolder,
    fileManagerData,
    tempFolder,
    handleSearchFiles,
    selectedFolder,
    preSearchText,
    onFileLoad,
  });

  // Web-specific state
  const [disabledRows, setDisabledRows] = useState<string[]>([]);
  const [columnDefinitions, setColumnDefinitions] = useState<object[]>(fileColumns);
  const [menuState, setMenuState] = useState<FileMenuState>({
    open: false,
    source: "",
    position: null,
    data: null,
    options: null,
  });
  const [isOver, setIsOver] = useState(false);

  // Refs
  const contextMenuSettingsRef = useRef<FileContextMenuSettings | null>(null);
  const isMounted = useRef(false);
  const dragCounter = useRef(0);
  // `searchRef`/`tempRef` are returned below but nothing destructures them; typed at their
  // (unused) intent rather than left as `any`.
  const searchRef = useRef<HTMLInputElement | null>(null);
  const tempRef = useRef<Record<string, unknown>>({});

  // Handlers
  const setSelectedRows = (indexes: (string | number)[], dataGrid: FilesGridRef) => {
    if (Array.isArray(indexes)) {
      if (filesViewMode === fileViewModes.DETAILED_VIEW) {
        // Row indexes are numbers; the handle's public signature is the wider `(string | number)[]`.
        dataGrid.current?.slickGrid?.setSelectedRows(indexes as number[]);
      } else if (filesViewMode === fileViewModes.LARGE_ICONS_VIEW) {
        if (indexes?.length == 0) {
          setSelectedFilesInIconsMode([]);
        } else {
          const filesToSelect: FileManagerRow[] = [];
          (indexes as number[]).forEach((index: number) => {
            if (filesData[index]) {
              // Guarded on the line above; `noUncheckedIndexedAccess` does not narrow a variable index.
              filesToSelect.push(filesData[index]!);
            }
          });
          setSelectedFilesInIconsMode(filesToSelect);
        }
      }
    }
  };

  const recordClick = (clickedRow: FileManagerRow) => {
    if (clickedRow.resourceType === "RESOURCE_TYPE_FOLDER" || clickedRow.resourceType === "RESOURCE_TYPE_TRANSMITTAL") {
      setSelectedFolder(clickedRow);
    }
    onFileClick(clickedRow);
  };

  const selectionHandlerSlickGrid = (dataGrid: FilesGridRef) => {
    const selectedData = dataGrid.current?.slickGrid
      ?.getSelectedRows()
      // Reached only through the `?.` chain above, so both are non-null here.
      .map((data: number) => dataGrid.current!.slickGrid!.getDataItem(data));
    const filteredData = selectedData?.filter(Boolean) || [];
    onFileSelect(filteredData);
  };

  const searchFileHandler = (args: React.ChangeEvent<HTMLInputElement> | string) => {
    const value = (typeof args === "string" ? args : args?.target?.value)?.toLowerCase() || "";
    setQuery(value);
  };

  const handleClose = () => {
    setMenuState((s: FileMenuState) => ({ ...s, source: "", open: false, position: null }));
  };

  const handleContextMenuForResource = (e: ContextMenuEvent, file: FileManagerRow, selectedFiles: FileManagerRow[]) => {
    // `resourceType` is optional on the row, and a row without one deliberately misses this test.
    let options = (["RESOURCE_TYPE_FOLDER", "RESOURCE_TYPE_TRANSMITTAL"] as (string | undefined)[]).includes(
      file.resourceType,
    )
      ? contextMenuSettingsRef.current?.folder
      : contextMenuSettingsRef.current?.file;

    // Options may declare an `extensions` allow-list (e.g. Export to PDF for dwg/dxf only).
    // Items without `extensions` always show; items with it show only for a matching file.
    const fileExt = (file?.name?.split(".").pop() || "").toLowerCase();
    options = options?.filter((opt: FileContextMenuItem) => !opt.extensions || opt.extensions.includes(fileExt));

    const disabledItems = menuOpen(selectedFiles);
    options = options?.map((opt: FileContextMenuItem) => {
      opt.disabled = false;
      if (disabledItems?.includes(opt.id)) {
        opt.disabled = true;
      }
      return opt;
    });
    setMenuState({
      // `options` is undefined until the settings object arrives, and `visible` with it; both read
      // as "closed" at runtime, so the state keeps its declared shape via erased assertions.
      options: options as FileContextMenuItem[],
      source: file.resourceType === "RESOURCE_TYPE_FOLDER" ? "FOLDER" : "FILE",
      open: contextMenuSettingsRef.current?.visible as boolean,
      position: {
        // Declared optional on `SlickEventData`; a real contextmenu event always carries them.
        top: e.pageY as number,
        left: e.pageX as number,
      },
      data: file,
    });
  };

  const contextMenuForIconsView = (e: React.MouseEvent, file: FileManagerRow) => {
    e.stopPropagation();
    e.preventDefault();
    const selectedFiles = [...selectedFilesInIconsMode];
    setSelectedFilesInIconsMode((prev: FileManagerRow[]) => [...prev, file]);
    handleContextMenuForResource(e, file, selectedFiles);
  };

  const handleGridCreated = (reactGrid: SlickgridReactInstance, _dataGrid: FilesGridRef) => {
    setIsViewRendered(true);
    reactGrid.slickGrid.onContextMenu.subscribe((e: SlickEventData) => {
      e.preventDefault();
      const cell = reactGrid.slickGrid.getCellFromEvent(e);
      if (!cell) return;

      const row = cell.row;
      const dataView = reactGrid.slickGrid.getData();
      const item = dataView.getItem(row);

      if (!reactGrid?.slickGrid?.getSelectedRows().includes(row)) {
        reactGrid.slickGrid.setSelectedRows([row]);
      }
      const selectedFiles = reactGrid.slickGrid
        .getSelectedRows()
        .map((data: number) => reactGrid.slickGrid.getDataItem(data));
      handleContextMenuForResource(e, item, selectedFiles);
    });
    reactGrid.slickGrid.onDblClick.subscribe((e: SlickEventData) => {
      e.preventDefault();
      const cell = reactGrid.slickGrid.getCellFromEvent(e);
      if (!cell) return;

      const row = cell.row;
      const dataView = reactGrid.slickGrid.getData();
      const item = dataView.getItem(row);
      recordClick(item);
    });
    reactGrid.slickGrid.onScroll.subscribe(() => {
      setMenuState((s: FileMenuState) => (s.open ? { ...s, source: "", open: false, position: null } : s));
    });
  };

  const onContextMenu = (args: React.MouseEvent) => {
    if (!(args.target as HTMLElement).closest(".slick-row")) {
      if (menuState.position) {
        return;
      }
      args.preventDefault();
      let options = contextMenuSettings?.layout || [];
      const disabledItems = menuOpen();
      options = options.map((opt: FileContextMenuItem) => {
        opt.disabled = false;
        if (disabledItems?.includes(opt.id)) {
          opt.disabled = true;
        }
        return opt;
      });
      setMenuState({
        options,
        source: "LAYOUT",
        // Undefined until the settings object arrives; MUI reads that as closed.
        open: contextMenuSettings?.visible as boolean,
        position: {
          top: args.pageY,
          left: args.pageX,
        },
      });
    }
  };

  const isFileDrag = (e: React.DragEvent) => Array.from(e.dataTransfer?.types || []).includes("Files");

  const onDragEnter = (e: React.DragEvent<HTMLElement>) => {
    if (!isFileDrag(e) || !allowDragAndDropUpload) return;
    e.preventDefault();
    dragCounter.current++;
    setIsOver(true);
  };

  const onDragLeave = (e: React.DragEvent<HTMLElement>) => {
    if (!isFileDrag(e) || !allowDragAndDropUpload) return;
    dragCounter.current = Math.max(0, dragCounter.current - 1);
    if (dragCounter.current === 0) {
      setIsOver(false);
    }
  };

  const onDragOver = (e: React.DragEvent<HTMLElement>) => {
    if (!isFileDrag(e) || !allowDragAndDropUpload) return;
    e.preventDefault();
    e.dataTransfer.dropEffect = "copy";
  };

  const handleDrop = (e: React.DragEvent<HTMLElement>) => {
    if (!isFileDrag(e) || !allowDragAndDropUpload) return;
    e.preventDefault();
    dragCounter.current = 0;
    setIsOver(false);
    beforeSending({
      action: "upload",
      files: e.dataTransfer.files,
    });
  };

  const handleMenuClick = (item: FileContextMenuItem, dataGrid: FilesGridRef) => {
    let selectedData: FileManagerRow[] = [];
    if (filesViewMode == fileViewModes.DETAILED_VIEW) {
      // KNOWN BUG, preserved verbatim: with no grid on the ref this assigns `undefined`, and the
      // `.map` below then throws. Erased assertion; a guard would change behaviour.
      selectedData = dataGrid.current?.slickGrid
        ?.getSelectedRows()
        .map((data: number) => dataGrid.current!.slickGrid!.getDataItem(data)) as FileManagerRow[];
    } else if (filesViewMode === fileViewModes.LARGE_ICONS_VIEW) {
      selectedData = selectedFilesInIconsMode;
    }

    if (item.id == "cut") {
      // `id` is optional only on the synthesised breadcrumb root; grid rows always carry one.
      const ids = selectedData.map((file: FileManagerRow) => file.id as string);
      setDisabledRows(ids);
    } else if (item.id == "paste") {
      setDisabledRows([]);
    } else if (
      item.id == "open" &&
      (selectedData[0]?.resourceType === "RESOURCE_TYPE_FOLDER" ||
        selectedData[0]?.resourceType === "RESOURCE_TYPE_TRANSMITTAL")
    ) {
      setSelectedFolder(selectedData[0]);
    }
    if (item.onClick) {
      item.onClick(selectedData);
    }
    setMenuState((s: FileMenuState) => ({ ...s, source: "", open: false, position: null }));
  };

  const checkIconBoxSelect = (args: React.ChangeEvent<HTMLInputElement>, file: FileManagerRow) => {
    let files = [...selectedFilesInIconsMode];
    if (args.target.checked) {
      files.push(file);
      setSelectedFilesInIconsMode([...files]);
    } else {
      files = files.filter((item) => item.id != file.id);
      setSelectedFilesInIconsMode([...files]);
    }
  };

  // Web-specific effects (shared logic is in useFileManagerShared)
  useEffect(() => {
    // @ts-expect-error -- `contextMenuSettings` is optional on this hook's props but required by the grid it is forwarded to; the file manager renders without a context menu when it is absent.
    contextMenuSettingsRef.current = contextMenuSettings;
  }, [contextMenuSettings]);

  // Clear web search input when folder changes
  useEffect(() => {
    // KNOWN BUG, preserved verbatim: assigning `null` to an input's `value` writes the string "null".
    // The `value: string | null` view names that; a `""` would change behaviour.
    const searchEle = document.getElementById("fileSearchBox") as (HTMLElement & { value: string | null }) | null;
    if (searchEle) {
      searchEle.value = null;
    }
  }, [selectedFolder?.id]);

  const fileColumnsKey = (fileColumns || [])
    .map((c: object) => {
      const col = c as { id?: string; initiallyHidden?: boolean };
      return `${col?.id}:${col?.initiallyHidden ? 1 : 0}`;
    })
    .join("|");

  useEffect(() => {
    setColumnDefinitions(() => [
      ...fileColumns,
      ...(showPathColumn
        ? [
            {
              id: "path",
              field: "path",
              name: "Path",
              // Slickgrid calls a formatter as (row, cell, value, columnDef, dataContext); the
              // parameter names here predate that and are kept verbatim - `value` is the ROW.
              formatter: (data: number, params: number, a: unknown, b: unknown, value: FileManagerRow) =>
                value.path ? `/${(value.path as string[]).join("/")}` : "/",
              minWidth: 200,
            },
          ]
        : []),
    ]);
  }, [showPathColumn, fileColumnsKey]);

  useEffect(() => {
    if (isMounted.current) {
      onFileSelect(selectedFilesInIconsMode);
    } else {
      isMounted.current = true;
    }
  }, [selectedFilesInIconsMode?.length]);

  return {
    // State
    disabledRows,
    setDisabledRows,
    breadCrumbData,
    setBreadCrumbData,
    filesData,
    setFilesData,
    showPathColumn,
    setShowPathColumn,
    isFirstRender,
    setIsFirstRender,
    columnDefinitions,
    setColumnDefinitions,
    menuState,
    setMenuState,
    debounced,
    setDebounced,
    query,
    setQuery,
    isOver,
    setIsOver,

    // Refs
    contextMenuSettingsRef,
    isMounted,
    dragCounter,
    searchRef,
    tempRef,

    // Handlers
    setSelectedRows,
    recordClick,
    selectionHandlerSlickGrid,
    searchFileHandler,
    handleClose,
    handleContextMenuForResource,
    contextMenuForIconsView,
    handleGridCreated,
    onContextMenu,
    isFileDrag,
    onDragEnter,
    onDragLeave,
    onDragOver,
    handleDrop,
    handleMenuClick,
    checkIconBoxSelect,

    // Translation
    t,

    // Props passthrough
    selectedFolder,
    filesViewMode,
    selectedFilesInIconsMode,
    disableDragAndDropUpload,
    allowDragAndDropUpload,
  };
}

export default useFileManagerFiles;
